import type { ReactNode } from 'react';
import { Link2 } from 'lucide-react';
import type { LibraryHistoryAction, LibraryHistoryEntry, LibrarySong, LinkProvider } from '../../services/songMatchMemory';
import { canonicalTrackUrl } from '../../utils/songLinks';

const ACTION_LABELS: Record<LibraryHistoryAction, string> = {
  create: 'Set',
  replace: 'Replaced',
  migrate: 'Imported',
  revert: 'Reinstated',
  wrong_song: 'Wrong song reported',
  owner_delete: 'Removed by the owner',
  review_approved: 'Approved by the owner',
};

function formatEntryDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

export interface SongLinkHistoryListProps {
  song: LibrarySong;
  provider: LinkProvider;
  entries: LibraryHistoryEntry[];
  currentDjUid: string;
  renderActions?: (entry: LibraryHistoryEntry) => ReactNode;
}

export function SongLinkHistoryList({ song, provider, entries, currentDjUid, renderActions }: SongLinkHistoryListProps) {
  const providerEntries = entries.filter(entry => entry.provider === provider);
  const currentTrackId = song.links[provider]?.trackId;

  if (providerEntries.length === 0) {
    return <p className="text-xs text-gray-400 py-1">No history for this player yet.</p>;
  }

  return (
    <ul className="space-y-1.5">
      {providerEntries.map(entry => {
        const url = canonicalTrackUrl(provider, entry.trackId);
        const date = formatEntryDate(entry.at);
        const isCurrent = entry.trackId === currentTrackId && !entry.deletedByOwner;
        return (
          <li
            key={entry.id}
            className={`p-2 rounded-lg border text-xs ${
              entry.deletedByOwner
                ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60'
                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
            }`}
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-gray-800 dark:text-gray-200">{ACTION_LABELS[entry.action]}</span>
                  {isCurrent && (
                    <span className="px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                      Current
                    </span>
                  )}
                  {entry.deletedByOwner && (
                    <span className="px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                      Removed by the owner
                    </span>
                  )}
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-primary hover:underline truncate block ${entry.deletedByOwner ? 'line-through' : ''}`}
                  title={url}
                >
                  <Link2 className="w-3 h-3 inline mr-1" />
                  {url}
                </a>
                <p className="text-gray-400 mt-0.5">
                  {entry.setBy === currentDjUid ? 'by you' : 'by another DJ'}
                  {date ? ` · ${date}` : ''}
                  {entry.eventId ? ' · at an event' : ''}
                </p>
              </div>
              {renderActions && <div className="flex items-center gap-1 flex-shrink-0">{renderActions(entry)}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
